import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import api from '../services/api';
import { formatTime } from '../utils/dateUtils';
import StatusBadge from '../components/StatusBadge';
import { useAuth } from '../context/AuthContext';

const COLORS = ['#F2652C', '#086F3C'];

const REGION_COLORS = {
  North:       '#086F3C',
  South:       '#F2652C',
  East:        '#1a73e8',
  West:        '#f9ab00',
  Central:     '#9334e9',
  'North-East':'#d93025',
};

const StatCard = ({ icon, label, value, color, iconBg, sub, trend }) => (
  <div className="col-6 col-lg-3">
    <div className="card border-0 shadow-sm rounded-4 h-100" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="card-body p-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="rounded-3 p-2" style={{ background: iconBg }}>
            <i className={`bi ${icon} fs-5`} style={{ color }}></i>
          </div>
          {trend != null && (
            <span className={`badge rounded-pill small ${trend >= 0 ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`}>
              <i className={`bi bi-arrow-${trend >= 0 ? 'up' : 'down'}-right me-1`}></i>{Math.abs(trend)}%
            </span>
          )}
        </div>
        <div className="fs-2 fw-bold lh-1 mb-1" style={{ color }}>{value ?? '—'}</div>
        <div className="fw-semibold small" style={{ color: '#231F20' }}>{label}</div>
        {sub && <div className="text-muted" style={{ fontSize: '0.72rem' }}>{sub}</div>}
      </div>
    </div>
  </div>
);

// Mini sparkline bar for office comparison table
const MiniBar = ({ value, max, color }) => (
  <div style={{ width: '100%', height: 6, background: '#f0f0f0', borderRadius: 4 }}>
    <div style={{ width: `${max ? Math.round((value / max) * 100) : 0}%`, height: '100%', background: color, borderRadius: 4, minWidth: value ? 4 : 0, transition: 'width 0.4s' }} />
  </div>
);

const Dashboard = () => {
  const { user, canViewAllOffices } = useAuth();
  const isGlobal = canViewAllOffices();

  const [stats,         setStats]         = useState(null);
  const [hourly,        setHourly]        = useState([]);
  const [recent,        setRecent]        = useState([]);
  const [officeStats,   setOfficeStats]   = useState([]);
  const [officeSummary, setOfficeSummary] = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [regionFilter,  setRegionFilter]  = useState('');
  const [officeSearch,  setOfficeSearch]  = useState('');
  const [sortBy,        setSortBy]        = useState('todayTotal');

  const fetchDashboard = useCallback(async () => {
    try {
      const requests = [
        api.get('/visits/dashboard'),
        isGlobal ? api.get('/offices/stats') : null,
      ];
      const [dashRes, officeRes] = await Promise.all(requests.filter(Boolean));

      const dash = dashRes.data;
      setStats(dash.stats);
      setRecent(dash.recentVisits);
      setHourly(
        dash.hourlyDistribution
          .filter(h => h.hour >= 6 && h.hour <= 22)
          .map(h => ({ hour: `${h.hour % 12 || 12}${h.hour < 12 ? 'am' : 'pm'}`, Visitors: h.count }))
      );

      if (officeRes) {
        setOfficeStats(officeRes.data.offices);
        setOfficeSummary(officeRes.data.summary);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [isGlobal]);

  useEffect(() => {
    fetchDashboard();
    const t = setInterval(fetchDashboard, 60_000);
    return () => clearInterval(t);
  }, [fetchDashboard]);

  const pieData = stats
    ? [{ name: 'Official', value: stats.officialToday }, { name: 'Personal', value: stats.personalToday }]
    : [];

  // Filtered + sorted office list
  const filteredOffices = officeStats
    .filter(o => {
      const matchR = !regionFilter || o.region === regionFilter;
      const matchQ = !officeSearch || o.name.toLowerCase().includes(officeSearch.toLowerCase()) || o.city.toLowerCase().includes(officeSearch.toLowerCase());
      return matchR && matchQ;
    })
    .sort((a, b) => b[sortBy] - a[sortBy]);

  const maxToday = Math.max(1, ...officeStats.map(o => o.todayTotal));

  const uniqueRegions = [...new Set(officeStats.map(o => o.region))].sort();

  const overallStats = isGlobal
    ? [
        { label: 'Total Visitors (All-Time)', value: stats?.totalAllTime,             color: 'primary'   },
        { label: 'Last 7 Days (All Offices)', value: officeSummary?.totalLast7Days,   color: 'secondary' },
        { label: 'Today Official',            value: officeSummary?.totalOfficialAll, color: 'success'   },
        { label: 'Today Personal',            value: officeSummary?.totalPersonalAll, color: 'warning'   },
      ]
    : [
        { label: 'Total Visitors All-Time', value: stats?.totalAllTime,   color: 'primary'   },
        { label: 'Completed Today',         value: stats?.completedToday, color: 'secondary' },
        { label: 'Active Right Now',        value: stats?.activeNow,      color: 'success'   },
      ];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <div className="text-center">
          <span className="spinner-border text-primary mb-3" style={{ width: 48, height: 48 }}></span>
          <p className="text-muted">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 px-3 px-lg-4">
      {/* Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-0">Dashboard</h4>
          <p className="text-muted mb-0 small">
            {isGlobal
              ? `All ${officeSummary?.totalOffices || ''} offices · `
              : (user?.office ? `${user.office.name} · ` : '')}
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={fetchDashboard}>
            <i className="bi bi-arrow-clockwise me-1"></i>Refresh
          </button>
          <Link to="/register" className="btn btn-primary btn-sm fw-semibold">
            <i className="bi bi-person-plus me-1"></i>New Entry
          </Link>
        </div>
      </div>

      {/* ── Global KPI cards (superadmin/heads) ── */}
      {isGlobal && officeSummary && (
        <div className="row g-3 mb-4">
          <StatCard icon="bi-buildings-fill"  color="#086F3C" iconBg="#e8f5ee" label="Total Offices"        value={officeSummary.totalOffices}    sub="active across all regions" />
          <StatCard icon="bi-people-fill"      color="#F2652C" iconBg="#fdf0eb" label="Today's Visitors"     value={officeSummary.totalTodayAll}   sub="all offices combined" />
          <StatCard icon="bi-door-open-fill"   color="#1a73e8" iconBg="#e8f0fe" label="Currently Inside"     value={officeSummary.totalActiveAll}  sub="active visits right now" />
          <StatCard icon="bi-graph-up-arrow"   color="#9334e9" iconBg="#f4e9ff" label="Last 30 Days"         value={officeSummary.totalLast30Days} sub="all offices" />
        </div>
      )}

      {/* ── Location-scoped KPI cards ── */}
      {!isGlobal && (
        <div className="row g-3 mb-4">
          <StatCard icon="bi-people-fill"    color="#F2652C" iconBg="#fdf0eb" label="Today's Visitors"  value={stats?.totalToday}    sub="registered today" />
          <StatCard icon="bi-door-open-fill" color="#086F3C" iconBg="#e8f5ee" label="Currently Inside"  value={stats?.activeNow}     sub="active visits" />
          <StatCard icon="bi-briefcase-fill" color="#F2652C" iconBg="#fdf0eb" label="Official Visits"   value={stats?.officialToday} sub="today" />
          <StatCard icon="bi-person-heart"   color="#086F3C" iconBg="#e8f5ee" label="Personal Visits"   value={stats?.personalToday} sub="today" />
        </div>
      )}

      {/* ── Multi-Office Comparison Table (global only) ── */}
      {isGlobal && filteredOffices.length > 0 && (
        <div className="card border-0 shadow-sm rounded-4 mb-4">
          <div className="card-header bg-transparent border-0 pt-4 px-4 pb-2">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div>
                <h6 className="fw-bold mb-0"><i className="bi bi-buildings me-2 text-primary"></i>Office-wise Visitor Activity</h6>
                <p className="text-muted small mb-0">Today's breakdown across all branches</p>
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {/* Region filter */}
                <div className="d-flex gap-1 flex-wrap">
                  <button className={`btn btn-sm rounded-pill ${!regionFilter ? 'btn-dark' : 'btn-outline-secondary'}`} onClick={() => setRegionFilter('')}>All</button>
                  {uniqueRegions.map(r => (
                    <button key={r} className="btn btn-sm rounded-pill"
                      style={regionFilter === r
                        ? { background: REGION_COLORS[r], color: '#fff', border: `1px solid ${REGION_COLORS[r]}` }
                        : { background: `${REGION_COLORS[r]}18`, color: REGION_COLORS[r], border: `1px solid ${REGION_COLORS[r]}44` }}
                      onClick={() => setRegionFilter(regionFilter === r ? '' : r)}>
                      {r}
                    </button>
                  ))}
                </div>
                {/* Search */}
                <div className="input-group input-group-sm" style={{ width: 180 }}>
                  <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted" style={{ fontSize: '0.7rem' }}></i></span>
                  <input type="text" className="form-control border-start-0 ps-0" placeholder="Search office…" value={officeSearch} onChange={e => setOfficeSearch(e.target.value)} />
                </div>
                {/* Sort */}
                <select className="form-select form-select-sm" style={{ width: 140 }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  <option value="todayTotal">Sort: Today</option>
                  <option value="activeNow">Sort: Active</option>
                  <option value="last7Days">Sort: 7 Days</option>
                  <option value="last30Days">Sort: 30 Days</option>
                </select>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4 py-3">#</th>
                    <th>Office</th>
                    <th>Region</th>
                    <th>City</th>
                    <th className="text-center">Today</th>
                    <th className="text-center">Active Now</th>
                    <th className="text-center d-none d-md-table-cell">Official</th>
                    <th className="text-center d-none d-md-table-cell">Personal</th>
                    <th className="text-center d-none d-lg-table-cell">7 Days</th>
                    <th className="text-center d-none d-lg-table-cell">30 Days</th>
                    <th className="pe-4 d-none d-xl-table-cell" style={{ minWidth: 120 }}>Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOffices.map((o, idx) => {
                    const rc = REGION_COLORS[o.region] || '#666';
                    return (
                      <tr key={o._id}>
                        <td className="ps-4 text-muted">{idx + 1}</td>
                        <td>
                          <div className="fw-semibold">{o.name}</div>
                          <div className="text-muted font-monospace" style={{ fontSize: '0.68rem' }}>{o.code}</div>
                        </td>
                        <td>
                          <span className="badge rounded-pill fw-semibold px-2 py-1"
                            style={{ background: `${rc}18`, color: rc, fontSize: '0.65rem', border: `1px solid ${rc}33` }}>
                            {o.region}
                          </span>
                        </td>
                        <td className="text-muted">{o.city}</td>
                        <td className="text-center">
                          <span className="fw-bold" style={{ color: o.todayTotal > 0 ? '#F2652C' : '#aaa' }}>{o.todayTotal}</span>
                        </td>
                        <td className="text-center">
                          {o.activeNow > 0
                            ? <span className="badge bg-success bg-opacity-10 text-success rounded-pill">{o.activeNow} inside</span>
                            : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center d-none d-md-table-cell text-muted">{o.todayOfficial || '—'}</td>
                        <td className="text-center d-none d-md-table-cell text-muted">{o.todayPersonal || '—'}</td>
                        <td className="text-center d-none d-lg-table-cell text-muted">{o.last7Days}</td>
                        <td className="text-center d-none d-lg-table-cell text-muted">{o.last30Days}</td>
                        <td className="pe-4 d-none d-xl-table-cell">
                          <MiniBar value={o.todayTotal} max={maxToday} color={rc} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredOffices.length === 0 && (
              <div className="text-center text-muted py-4">
                <i className="bi bi-buildings opacity-25 d-block fs-1 mb-2"></i>No offices match filter
              </div>
            )}
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="row g-4 mb-4">
        {/* Hourly Bar Chart */}
        <div className="col-12 col-lg-8">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
              <h6 className="fw-bold mb-0"><i className="bi bi-bar-chart-fill text-primary me-2"></i>Peak Hours — Today</h6>
              <p className="text-muted small mb-0">
                {isGlobal ? 'Combined visitor count by hour across all offices' : 'Visitor count by hour of the day'}
              </p>
            </div>
            <div className="card-body p-4">
              {hourly.length === 0 ? (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted" style={{ minHeight: 200 }}>
                  <div className="text-center">
                    <i className="bi bi-bar-chart fs-1 opacity-25 d-block mb-2"></i>
                    No visitor data yet today
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={hourly} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,.1)' }}
                      formatter={(v) => [v, 'Visitors']}
                    />
                    <Bar dataKey="Visitors" fill="#F2652C" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Purpose Pie / Region breakdown */}
        <div className="col-12 col-lg-4">
          {isGlobal && officeStats.length > 0 ? (
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
                <h6 className="fw-bold mb-0"><i className="bi bi-pie-chart-fill text-primary me-2"></i>By Region — Today</h6>
                <p className="text-muted small mb-0">Visitor distribution by region</p>
              </div>
              <div className="card-body p-4">
                {uniqueRegions.map(r => {
                  const total = officeStats.filter(o => o.region === r).reduce((s, o) => s + o.todayTotal, 0);
                  const overall = officeSummary?.totalTodayAll || 1;
                  const pct = Math.round((total / overall) * 100);
                  const c = REGION_COLORS[r] || '#666';
                  return (
                    <div key={r} className="mb-3">
                      <div className="d-flex align-items-center justify-content-between small mb-1">
                        <span className="fw-semibold" style={{ color: c }}>{r}</span>
                        <span className="text-muted">{total} <span className="text-muted opacity-75">({pct}%)</span></span>
                      </div>
                      <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4 }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: c, borderRadius: 4, minWidth: total ? 4 : 0 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
                <h6 className="fw-bold mb-0"><i className="bi bi-pie-chart-fill text-primary me-2"></i>Visit Purpose</h6>
                <p className="text-muted small mb-0">Official vs Personal today</p>
              </div>
              <div className="card-body p-4 d-flex align-items-center justify-content-center">
                {stats?.totalToday === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-pie-chart fs-1 opacity-25 d-block mb-2"></i>No data yet
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                      </Pie>
                      <Legend formatter={(v) => <span style={{ fontSize: 12 }}>{v}</span>} />
                      <Tooltip formatter={(v) => [v, 'Visitors']} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Overall Stats + Recent Visits */}
      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
              <h6 className="fw-bold mb-0"><i className="bi bi-graph-up text-primary me-2"></i>Overall Statistics</h6>
            </div>
            <div className="card-body p-4">
              {overallStats.map(({ label, value, color }) => (
                <div key={label} className="d-flex align-items-center justify-content-between py-3 border-bottom last-border-0">
                  <span className="small text-muted">{label}</span>
                  <span className={`fw-bold text-${color} fs-5`}>{value ?? 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-8">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0 d-flex align-items-center justify-content-between">
              <div>
                <h6 className="fw-bold mb-0"><i className="bi bi-clock-history text-primary me-2"></i>Recent Entries Today</h6>
              </div>
              <Link to="/records" className="btn btn-sm btn-outline-primary">View All</Link>
            </div>
            <div className="card-body p-0">
              {recent.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-inbox fs-1 opacity-25 d-block mb-2"></i>No visitor entries today
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0 small">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-4 py-3">Visitor</th>
                        <th>Mobile</th>
                        <th>Meeting</th>
                        {isGlobal && <th className="d-none d-md-table-cell">Office</th>}
                        <th>Time In</th>
                        <th className="pe-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map(v => (
                        <tr key={v._id}>
                          <td className="ps-4">
                            <div className="d-flex align-items-center gap-2">
                              <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center fw-bold text-primary flex-shrink-0" style={{ width: 34, height: 34, fontSize: 13 }}>
                                {v.visitor?.name?.[0]?.toUpperCase()}
                              </div>
                              <div>
                                <div className="fw-semibold">{v.visitor?.name}</div>
                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>{v.purpose}</div>
                              </div>
                            </div>
                          </td>
                          <td className="font-monospace text-muted">{v.visitor?.phone}</td>
                          <td>{v.toWhomMeet}</td>
                          {isGlobal && (
                            <td className="d-none d-md-table-cell">
                              <span className="badge bg-light text-dark border" style={{ fontSize: '0.65rem' }}>{v.office?.code || '—'}</span>
                            </td>
                          )}
                          <td>{formatTime(v.timeIn)}</td>
                          <td className="pe-4"><StatusBadge status={v.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
